<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card class="elevation-0 rounded-0 fill-height">
        <!-- Toolbar -->
        <v-toolbar flat dark elevation="0" class="modal-header-gradient">
          <v-btn icon class="modal-close-btn" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="font-weight-bold">
            <v-icon left>mdi-shield-key</v-icon>
            Buy Tokens
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn outlined rounded @click="dialog = false">Close</v-btn>
        </v-toolbar>

        <div class="modal-body grey lighten-5">
          <div class="content-wrapper">
            <!-- Info Alert -->
            <v-alert
              border="left"
              colored-border
              color="info"
              icon="mdi-information"
              class="mt-6 rounded-lg"
              elevation="1"
            >
              In MYSCH, starting from June 2025, we will use
              <strong>TOKENS</strong> for Premium Services. To learn how it
              works,
              <a
                href="https://myschng.tawk.help/article/how-tokens-work-on-mysch-results-portal"
                target="_blank"
                class="info--text text-decoration-underline font-weight-bold"
                >click here</a
              >.
            </v-alert>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-16">
              <v-progress-circular
                indeterminate
                color="primary"
                size="48"
              ></v-progress-circular>
              <p class="grey--text mt-4">Loading available plans...</p>
            </div>

            <template v-else>
              <!-- Partner Notice -->
              <div v-if="attachedToPartner" class="my-8">
                <div class="text-center mb-6">
                  <v-avatar color="amber lighten-4" size="64" class="mb-3">
                    <v-icon color="amber darken-3" size="32"
                      >mdi-account-supervisor</v-icon
                    >
                  </v-avatar>
                  <h2 class="font-weight-bold">Managed by a MYSCH Partner</h2>
                  <p class="grey--text mb-0">
                    Your school's tokens are managed by a partner. Please
                    purchase your tokens directly from them using the
                    details below.
                  </p>
                </div>

                <v-card
                  v-if="partner"
                  outlined
                  class="pa-6 rounded-xl partner-card mx-auto"
                  max-width="520"
                >
                  <div class="d-flex align-center mb-4">
                    <v-avatar color="primary lighten-5" size="56" class="mr-4">
                      <v-icon color="primary" size="28">mdi-domain</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-h6 font-weight-bold">
                        {{ partner.company_name }}
                      </div>
                      <div class="caption grey--text">Your MYSCH Partner</div>
                    </div>
                  </div>

                  <v-divider class="mb-4"></v-divider>

                  <div class="d-flex align-center mb-3">
                    <v-icon color="primary" class="mr-3">mdi-account</v-icon>
                    <span>{{ partner.contact_person }}</span>
                  </div>
                  <div class="d-flex align-center mb-3">
                    <v-icon color="primary" class="mr-3">mdi-phone</v-icon>
                    <a
                      :href="`tel:${partner.phone}`"
                      class="text-decoration-none"
                      >{{ partner.phone }}</a
                    >
                  </div>
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-email</v-icon>
                    <a
                      :href="`mailto:${partner.email}`"
                      class="text-decoration-none"
                      >{{ partner.email }}</a
                    >
                  </div>
                </v-card>
              </div>

              <template v-else>
                <!-- Token Plans -->
                <div class="my-8">
                  <div class="text-center mb-6">
                    <h2 class="font-weight-bold">Available Plans</h2>
                    <p class="grey--text mb-0">
                      Pick the package that suits your school's needs
                    </p>
                  </div>

                  <v-row v-if="plans.length" dense justify="center">
                    <v-col
                      v-for="plan in plans"
                      :key="plan.id"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="3"
                    >
                      <v-card
                        class="plan-card pa-5 rounded-xl text-center hover-elevation-12 transition-all"
                        :class="{
                          'plan-card--selected':
                            selectedPlan && selectedPlan.id === plan.id,
                        }"
                        :elevation="
                          selectedPlan && selectedPlan.id === plan.id ? 6 : 1
                        "
                        outlined
                      >
                        <v-chip
                          v-if="plan.id === bestValuePlanId"
                          color="success"
                          text-color="white"
                          x-small
                          label
                          class="best-value-chip font-weight-bold"
                        >
                          BEST VALUE
                        </v-chip>

                        <v-avatar
                          color="primary lighten-5"
                          size="56"
                          class="mb-3"
                        >
                          <v-icon color="primary" size="28"
                            >mdi-shield-key</v-icon
                          >
                        </v-avatar>

                        <h4 class="font-weight-bold">
                          {{ plan.plan_name }} Plan
                        </h4>

                        <p class="text-h4 font-weight-bold primary--text mb-0 mt-2">
                          {{ plan.count }}
                        </p>
                        <p class="caption grey--text mb-3">Tokens</p>

                        <v-chip
                          small
                          outlined
                          color="grey darken-1"
                          class="mb-4"
                        >
                          &#8358;{{ (plan.amount / plan.count).toFixed(2) }} / token
                        </v-chip>

                        <v-btn
                          block
                          large
                          :color="
                            selectedPlan && selectedPlan.id === plan.id
                              ? 'success'
                              : 'primary'
                          "
                          class="rounded-pill font-weight-bold"
                          elevation="2"
                          @click="selectPlan(plan)"
                        >
                          <v-icon
                            v-if="selectedPlan && selectedPlan.id === plan.id"
                            left
                            small
                          >
                            mdi-check-circle
                          </v-icon>
                          {{ formatPayment(plan.amount) }}
                        </v-btn>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-alert
                    v-else
                    type="warning"
                    outlined
                    border="left"
                    class="rounded-lg mx-auto"
                    max-width="520"
                  >
                    No token plans are available right now. Please contact
                    support.
                  </v-alert>
                </div>

                <!-- Payment Partners -->
                <div v-if="selectedPlan" id="payment-partners" class="my-8">
                  <v-alert
                    color="success"
                    dark
                    class="rounded-lg d-flex align-center mb-6"
                    dense
                  >
                    <v-icon left>mdi-check-circle</v-icon>
                    <span>
                      You selected the
                      <strong>{{ selectedPlan.plan_name }} Plan</strong>
                      &mdash; {{ selectedPlan.count }} Tokens for
                      <strong>{{ formatPayment(selectedPlan.amount) }}</strong>
                    </span>
                  </v-alert>

                  <div class="text-center mb-6">
                    <h3 class="font-weight-bold">Payment Partners</h3>
                    <p class="grey--text mb-0">
                      Pay to any of the following accounts, then send proof
                      of payment
                    </p>
                  </div>

                  <v-row dense justify="center">
                    <v-col
                      v-for="(account, index) in bankAccounts"
                      :key="index"
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-card outlined class="pa-4 rounded-lg bank-card">
                        <div class="d-flex align-center mb-3">
                          <v-avatar
                            color="primary lighten-5"
                            size="40"
                            class="mr-3"
                          >
                            <v-icon color="primary">mdi-bank</v-icon>
                          </v-avatar>
                          <div class="font-weight-bold">{{ account.bank }}</div>
                        </div>
                        <div class="caption grey--text">Account Name</div>
                        <div class="font-weight-medium mb-3">
                          {{ account.name }}
                        </div>
                        <div class="caption grey--text">Account Number</div>
                        <div class="d-flex align-center">
                          <div class="font-weight-bold text-h6 mr-2">
                            {{ account.number }}
                          </div>
                          <v-btn
                            icon
                            small
                            color="primary"
                            @click="copyToClipboard(account.number)"
                          >
                            <v-icon small>mdi-content-copy</v-icon>
                          </v-btn>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-card
                    color="red lighten-5"
                    flat
                    class="rounded-lg mt-6 pa-4 text-center mx-auto"
                    max-width="600"
                  >
                    <p class="mb-2 red--text text--darken-2">
                      After payment, send your receipt &amp; school name via
                      WhatsApp
                    </p>
                    <v-btn
                      color="green darken-1"
                      dark
                      class="rounded-pill font-weight-bold"
                      href="https://wa.me/2347081307643"
                      target="_blank"
                    >
                      <v-icon left>mdi-whatsapp</v-icon>
                      Chat on WhatsApp &mdash; 0708 130 7643
                    </v-btn>
                  </v-card>
                </div>
              </template>
            </template>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from "axios";

export default {
  name: "BuyTokenModal",
  data() {
    return {
      dialog: true,
      loading: true,
      partners: [],
      plans: [],
      selectedPlan: null, // Track which plan is selected
      attachedToPartner: false,
      partner: null,
      authToken: sessionStorage.getItem("authToken") ?? null,
      school: localStorage.school ? JSON.parse(localStorage.school) : null,
      bankAccounts: [
        {
          name: "Ogechukwu Happiness",
          number: "08132250093",
          bank: "OPAY",
        }
      ],
    };
  },
  computed: {
    bestValuePlanId() {
      if (!this.plans.length) return null;
      return this.plans.reduce((best, plan) => {
        const rate = plan.amount / plan.count;
        return !best || rate < best.rate ? { id: plan.id, rate } : best;
      }, null)?.id;
    },
  },
  methods: {
    selectPlan(plan) {
      this.selectedPlan = plan;
      this.$nextTick(() => {
        const el = document.getElementById("payment-partners");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      });
    },
    fetchPaymentPartners() {
      axios
        .get(`${process.env.VUE_APP_BASEURL}/payment-partners`, {
          headers: { Authorization: `Bearer ${this.authToken}` },
        })
        .then((response) => {
          this.partners = response.data.data;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    fetchTokenPlans() {
      axios
        .get(
          `${process.env.VUE_APP_BASEURL}/token-plans?school_id=${this.school?.school_id}`,
          {
            headers: { Authorization: `Bearer ${this.authToken}` },
          }
        )
        .then((response) => {
          this.attachedToPartner = response.data.attached_to_partner;
          this.partner = response.data.partner;
          this.plans = response.data.data;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatPayment(value) {
      const numberValue = parseFloat(value);
      if (isNaN(numberValue)) return "₦0.00"; // or return 'N/A'
      return `₦${numberValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$toast.success("Copied to clipboard", { timeout: 2000 });
        })
        .catch(() => {
          this.$toast.error("Unable to copy. Please copy manually.", {
            timeout: 2000,
          });
        });
    },
  },
  mounted() {
    this.fetchPaymentPartners();
    this.fetchTokenPlans();
  },
};
</script>

<style scoped>
.modal-body {
  min-height: 100%;
}
.content-wrapper {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 16px 56px;
}
.text-decoration-underline {
  text-decoration: underline;
}
.text-decoration-none {
  text-decoration: none;
}
.hover-elevation-12:hover {
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  transition: box-shadow 0.3s ease;
}
.plan-card {
  position: relative;
  border: 2px solid transparent;
  background-color: #fff;
}
.plan-card--selected {
  border-color: var(--v-success-base, #4caf50);
}
.best-value-chip {
  position: absolute;
  top: -10px;
  right: 16px;
}
.partner-card {
  background-color: #fff;
}
.bank-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  background-color: #fff;
}
.bank-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-2px);
}
</style>
